import { expectArray, expectBoolean, expectRecord, expectString } from "./schema";
import { parseStudentBillingCurrency, parseStudentBillingDate, parseStudentBillingIdentifier, parseStudentBillingServiceMonth } from "./student-billing";
export const STUDENT_BILLING_CUSTOMER_INVOICE_STATUSES = [
    "prepared", "paymentPending", "processing", "paid", "failed", "needsAttention"
];
function exact(record, allowed, path) {
    const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
    if (unknown.length)
        throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
}
function integer(value, path, minimum = 0) {
    if (!Number.isSafeInteger(value) || Number(value) < minimum)
        throw new Error(`${path} is invalid`);
    return Number(value);
}
function fingerprint(value, path) {
    const parsed = expectString(value, path);
    if (!/^[a-f0-9]{64}$/u.test(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
function iso(value, path) {
    const parsed = expectString(value, path);
    if (!Number.isFinite(new Date(parsed).getTime()))
        throw new Error(`${path} is invalid`);
    return new Date(parsed).toISOString();
}
export function parsePrepareStudentBillingCardInvoicesRequest(value, path = "studentBillingCardInvoicePrepareRequest") {
    const record = expectRecord(value, path);
    exact(record, ["expectedPreparationVersion"], path);
    return {
        expectedPreparationVersion: record.expectedPreparationVersion === null
            ? null
            : integer(record.expectedPreparationVersion, `${path}.expectedPreparationVersion`, 1)
    };
}
export function parseStudentBillingCustomerInvoice(value, path = "studentBillingCustomerInvoice") {
    const record = expectRecord(value, path);
    exact(record, [
        "customerInvoiceId", "tenantId", "serviceMonth", "payerId", "payerSafeDisplayLabel",
        "scheduledChargeDate", "currency", "billingUnitIds", "studentCount", "grossAmount",
        "invoiceEligibleAmount", "platformFeePolicyKey", "feeRateBps", "applicationFeeWaived",
        "applicationFeeAmount", "sourceFingerprint", "status", "preparationVersion",
        "preparedByPlatformUserId", "preparedAt"
    ], path);
    const billingUnitIds = expectArray(record.billingUnitIds, parseStudentBillingIdentifier, `${path}.billingUnitIds`);
    if (billingUnitIds.length === 0 || billingUnitIds.length > 100 || new Set(billingUnitIds).size !== billingUnitIds.length) {
        throw new Error(`${path}.billingUnitIds is invalid`);
    }
    const status = expectString(record.status, `${path}.status`);
    if (!STUDENT_BILLING_CUSTOMER_INVOICE_STATUSES.includes(status))
        throw new Error(`${path}.status is invalid`);
    const grossAmount = integer(record.grossAmount, `${path}.grossAmount`, 1);
    const eligible = integer(record.invoiceEligibleAmount, `${path}.invoiceEligibleAmount`, 1);
    if (grossAmount !== eligible)
        throw new Error(`${path}.invoiceEligibleAmount is invalid`);
    const feeRateBps = integer(record.feeRateBps, `${path}.feeRateBps`, 1);
    const applicationFeeWaived = expectBoolean(record.applicationFeeWaived, `${path}.applicationFeeWaived`);
    const applicationFeeAmount = integer(record.applicationFeeAmount, `${path}.applicationFeeAmount`);
    if (feeRateBps > 10_000 || applicationFeeAmount !== studentBillingApplicationFeeAmount({ invoiceEligibleAmount: eligible, feeRateBps, applicationFeeWaived })) {
        throw new Error(`${path}.applicationFeeAmount is invalid`);
    }
    return {
        customerInvoiceId: parseStudentBillingIdentifier(record.customerInvoiceId, `${path}.customerInvoiceId`),
        tenantId: parseStudentBillingIdentifier(record.tenantId, `${path}.tenantId`),
        serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        payerId: parseStudentBillingIdentifier(record.payerId, `${path}.payerId`),
        payerSafeDisplayLabel: expectString(record.payerSafeDisplayLabel, `${path}.payerSafeDisplayLabel`),
        scheduledChargeDate: parseStudentBillingDate(record.scheduledChargeDate, `${path}.scheduledChargeDate`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        billingUnitIds,
        studentCount: integer(record.studentCount, `${path}.studentCount`, 1),
        grossAmount,
        invoiceEligibleAmount: eligible,
        platformFeePolicyKey: fingerprint(record.platformFeePolicyKey, `${path}.platformFeePolicyKey`),
        feeRateBps,
        applicationFeeWaived,
        applicationFeeAmount,
        sourceFingerprint: fingerprint(record.sourceFingerprint, `${path}.sourceFingerprint`),
        status: status,
        preparationVersion: integer(record.preparationVersion, `${path}.preparationVersion`, 1),
        preparedByPlatformUserId: parseStudentBillingIdentifier(record.preparedByPlatformUserId, `${path}.preparedByPlatformUserId`),
        preparedAt: iso(record.preparedAt, `${path}.preparedAt`)
    };
}
export function parseStudentBillingCardInvoicePreparation(value, path = "studentBillingCardInvoicePreparation") {
    const record = expectRecord(value, path);
    exact(record, [
        "tenantId", "serviceMonth", "sourceFingerprint", "version", "invoiceCount", "billingUnitCount",
        "grossAmount", "applicationFeeAmount", "preparedByPlatformUserId", "preparedAt"
    ], path);
    return {
        tenantId: parseStudentBillingIdentifier(record.tenantId, `${path}.tenantId`),
        serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        sourceFingerprint: fingerprint(record.sourceFingerprint, `${path}.sourceFingerprint`),
        version: integer(record.version, `${path}.version`, 1),
        invoiceCount: integer(record.invoiceCount, `${path}.invoiceCount`, 1),
        billingUnitCount: integer(record.billingUnitCount, `${path}.billingUnitCount`, 1),
        grossAmount: integer(record.grossAmount, `${path}.grossAmount`, 1),
        applicationFeeAmount: integer(record.applicationFeeAmount, `${path}.applicationFeeAmount`),
        preparedByPlatformUserId: parseStudentBillingIdentifier(record.preparedByPlatformUserId, `${path}.preparedByPlatformUserId`),
        preparedAt: iso(record.preparedAt, `${path}.preparedAt`)
    };
}
export function studentBillingApplicationFeeAmount(input) {
    const amount = integer(input.invoiceEligibleAmount, "invoiceEligibleAmount", 1);
    const rate = integer(input.feeRateBps, "feeRateBps", 1);
    if (rate > 10_000)
        throw new Error("feeRateBps is invalid");
    if (input.applicationFeeWaived)
        return 0;
    const calculated = (BigInt(amount) * BigInt(rate)) / 10000n;
    if (calculated > BigInt(Number.MAX_SAFE_INTEGER))
        throw new Error("applicationFeeAmount is invalid");
    return Number(calculated);
}
export function studentBillingSafeAmountSum(values, path = "amounts") {
    const sum = values.reduce((total, value, index) => total + BigInt(integer(value, `${path}[${index}]`)), 0n);
    if (sum > BigInt(Number.MAX_SAFE_INTEGER))
        throw new Error(`${path} exceeds the safe integer range`);
    return Number(sum);
}
